/**
 * Shared redirect path normalization, matching, and suggestion helpers.
 */

export function normalizeRedirectPath(path) {
  if (!path || typeof path !== 'string') return '/'
  let p = path.trim()
  if (!p.startsWith('/')) p = '/' + p
  p = p.replace(/\/+/g, '/')
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)
  return p
}

export function pathVariants(path) {
  const normalized = normalizeRedirectPath(path)
  const withSlash = normalized.endsWith('/') ? normalized : normalized + '/'
  const noSlash = normalized !== '/' && normalized.endsWith('/') ? normalized.slice(0, -1) : normalized
  return Array.from(new Set([normalized, noSlash, withSlash]))
}

export function encodeMissingPathId(path) {
  const normalized = normalizeRedirectPath(path)
  return encodeURIComponent(normalized).replace(/\//g, '__')
}

export function decodeMissingPathId(id) {
  try {
    return decodeURIComponent(String(id).replace(/__/g, '/'))
  } catch (e) {
    return '/'
  }
}

/**
 * Month bucket key (YYYY-MM) used for redirect-hit time-series counters.
 */
export function monthKey(date = new Date()) {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

/**
 * Given a redirectHits document ({ total, months: { 'YYYY-MM': n } }), derive
 * usage totals for rolling windows so we can decide whether a redirect is still
 * earning its keep.
 */
export function summarizeRedirectHits(data, now = new Date()) {
  const result = { total: 0, last6Months: 0, last12Months: 0 }
  if (!data) return result

  result.total = Number(data.total) || 0

  const months = data.months && typeof data.months === 'object' ? data.months : {}
  const keys6 = new Set()
  const keys12 = new Set()
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
    const key = monthKey(d)
    keys12.add(key)
    if (i < 6) keys6.add(key)
  }
  Object.keys(months).forEach((key) => {
    const n = Number(months[key]) || 0
    if (keys12.has(key)) result.last12Months += n
    if (keys6.has(key)) result.last6Months += n
  })

  // Fall back to total if monthly buckets are missing but a total exists.
  if (!Object.keys(months).length && result.total) {
    result.last6Months = result.total
    result.last12Months = result.total
  }

  return result
}

export function timestampToMs(ts) {
  if (!ts) return 0
  try {
    if (ts.seconds) return ts.seconds * 1000
    const n = Number(ts)
    if (!Number.isNaN(n)) return n
    const d = new Date(ts)
    return d.getTime() || 0
  } catch (e) {
    return 0
  }
}

export function isBotUserAgent(userAgent) {
  if (!userAgent || typeof userAgent !== 'string') return false
  return /bot|crawler|spider|slurp|bingpreview|googlebot|yandex|baidu|duckduckbot|facebookexternalhit|semrush|ahrefs|mj12bot|petalbot|bytespider/i.test(userAgent)
}

function resolveRedirectNextPath(redirect, resolveRoute) {
  if (!redirect || !redirect.published) return null
  if (redirect.type === 'path' && typeof redirect.to === 'string') {
    return normalizeRedirectPath(redirect.to)
  }
  if (redirect.type === 'route' && typeof redirect.to === 'string' && typeof resolveRoute === 'function') {
    try {
      const resolved = resolveRoute({ name: redirect.to, params: redirect.params || {} })
      const href = (resolved && resolved.route && resolved.route.path) || (resolved && resolved.href) || ''
      if (href && href.startsWith('/')) return normalizeRedirectPath(href.split('?')[0])
    } catch (e) {}
  }
  return null
}

export function detectRedirectCycle(from, to, type, allRedirects, resolveRoute) {
  const start = normalizeRedirectPath(from)
  const redirectsByFrom = {}
  ;(allRedirects || []).forEach((r) => {
    if (!r || !r.from) return
    redirectsByFrom[normalizeRedirectPath(r.from)] = r
  })

  const draft = {
    from: start,
    to,
    type,
    published: true,
    params: null,
  }
  redirectsByFrom[start] = draft

  const visited = new Set()
  let current = start
  const maxSteps = Math.max(20, Object.keys(redirectsByFrom).length + 5)

  for (let i = 0; i < maxSteps; i += 1) {
    if (visited.has(current)) return true
    visited.add(current)

    const redirect = redirectsByFrom[current]
    const next = resolveRedirectNextPath(redirect, resolveRoute)
    if (!next) return false
    if (next === start) return true
    current = next
  }

  return visited.has(current)
}

function levenshtein(a, b) {
  const s = String(a || '')
  const t = String(b || '')
  const m = s.length
  const n = t.length
  if (m === 0) return n
  if (n === 0) return m

  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
  for (let i = 0; i <= m; i += 1) dp[i][0] = i
  for (let j = 0; j <= n; j += 1) dp[0][j] = j

  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      )
    }
  }
  return dp[m][n]
}

export function stringSimilarity(a, b) {
  const left = normalizeRedirectPath(a).toLowerCase()
  const right = normalizeRedirectPath(b).toLowerCase()
  if (!left || !right) return 0
  if (left === right) return 1

  const leftSegments = left.split('/').filter(Boolean)
  const rightSegments = right.split('/').filter(Boolean)
  const segmentBonus = leftSegments.length && rightSegments.length
    ? leftSegments.filter((seg, idx) => rightSegments[idx] === seg).length / Math.max(leftSegments.length, rightSegments.length)
    : 0

  const maxLen = Math.max(left.length, right.length)
  const distance = levenshtein(left, right)
  const editScore = maxLen ? 1 - distance / maxLen : 0

  return Math.max(editScore, segmentBonus * 0.95)
}

export function suggestRedirectTarget(missingPath, candidates) {
  let best = null
  let bestScore = 0

  ;(candidates || []).forEach((candidate) => {
    if (!candidate || !candidate.value) return
    const score = stringSimilarity(missingPath, candidate.value)
    if (score > bestScore) {
      bestScore = score
      best = candidate
    }
  })

  if (!best || bestScore < 0.35) return null
  return { ...best, score: bestScore }
}

export function aggregateMissingPathDocs(docs) {
  const stats = {}

  ;(docs || []).forEach(({ id, data }) => {
    const item = data || {}
    const path = normalizeRedirectPath(item.path || item.fullPath || decodeMissingPathId(id))
    const encodedId = encodeMissingPathId(path)
    const isCounterDoc = id === encodedId || typeof item.hits === 'number'

    if (!stats[path]) {
      stats[path] = {
        path,
        hits: 0,
        lastSeen: null,
        lastReferrer: '',
        lastUserAgent: '',
        lastQuery: null,
        lastHash: '',
        docId: encodedId,
        isLegacy: false,
      }
    }

    if (isCounterDoc) {
      stats[path].hits += Number(item.hits) || 0
      stats[path].docId = encodedId
    } else {
      stats[path].hits += 1
      stats[path].isLegacy = true
    }

    const ts = item.lastSeen || item.createdAt || null
    const prevMs = timestampToMs(stats[path].lastSeen)
    const curMs = timestampToMs(ts)
    if (curMs >= prevMs) {
      stats[path].lastSeen = ts || stats[path].lastSeen
      if (item.lastReferrer || item.referrer) stats[path].lastReferrer = item.lastReferrer || item.referrer || ''
      if (item.lastUserAgent || item.userAgent) stats[path].lastUserAgent = item.lastUserAgent || item.userAgent || ''
      if (item.lastQuery || item.query) stats[path].lastQuery = item.lastQuery || item.query || null
      if (item.lastHash || item.hash) stats[path].lastHash = item.lastHash || item.hash || ''
    }
  })

  return Object.values(stats).sort((a, b) => {
    if (a.hits === b.hits) {
      return timestampToMs(a.lastSeen) < timestampToMs(b.lastSeen) ? 1 : -1
    }
    return a.hits < b.hits ? 1 : -1
  })
}
